import apiClient from "./client";

// Common response type for paginated responses
interface PaginatedResponse<T> {
  success: boolean;
  message: string;
  data: T[];
  pagination: {
    limit: number;
    page: number;
    total: number;
    totalPages: number;
  };
}

export interface Schedule {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  days: Array<string>;

  isActive: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
  show: {
    id: string;
    name: string;
    hostName: string;
    description?: string;
    thumbnail?: string;
    isActive: boolean;
    isSpecial:boolean;
    isFeatured:boolean;
    categories: Array<{
      id: string;
      name: string;
      isActive: boolean;
    }>;
  };
  gradient?: string
}

export interface CreateScheduleData {
  showId: string;
  date: string;
  startTime: string;
  endTime: string;
  days: string[];
  isActive?: boolean;
  status?: string;
  isSpecial?: boolean;
  isFeatured?: boolean;
}

export interface UpdateScheduleData extends Partial<Omit<CreateScheduleData, 'categories'>> {
  id: string;
  categories?: string[];
}

export interface ScheduleFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  isActive?: boolean;
  isSpecial?: boolean;
  isFeatured?: boolean;
  startDate?: string;
  endDate?: string;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface LandingPageData {
  scheduleItems: Schedule[];
  featuredShows: Array<{
    id: string;
    name: string;
    hostName: string;
    description?: string;
    thumbnail?: string;
    isActive: boolean;
    categories: Array<{
      id: string;
      name: string;
      isActive: boolean;
    }>;
  }>;
  historyItems: Schedule[];
}

export const scheduleApi = {
  // Create a new schedule
  async createSchedule(scheduleData: CreateScheduleData): Promise<{ success: string; message: string; data: Schedule }> {
    const response = await apiClient.post<{ success: string; message: string; data: Schedule }>('/schedules', scheduleData);
    return response.data;
  },

  // Get a single schedule by ID
  async getSchedule(id: string): Promise<Schedule> {
    const response = await apiClient.get<{ data: Schedule }>(`/schedules/${id}`);
    return response.data.data;
  },

  // Get all schedules with optional filters and pagination
  async getSchedules(params: {
    page: number;
    limit: number;
    search?: string;
    status?: string;
    isActive?: boolean;
    isSpecial?: boolean;
    isFeatured?: boolean;
    sortField?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Schedule>> {
    const response = await apiClient.get<PaginatedResponse<Schedule>>('/schedules', { params });
    return response.data;
  },

  // Update a schedule
  async updateSchedule({ id, ...updateData }: UpdateScheduleData): Promise<{ success: string; message: string; data: Schedule }> {
    const response = await apiClient.patch<{ success: string; message: string; data: Schedule }>(`/schedules/${id}`, updateData);
    return response.data;
  },

  // Delete a schedule
  async deleteSchedule(id: string): Promise<{
    success: string;
    message: string
    data: Schedule
  }> {
    const response = await apiClient.patch<{ success: string; message: string; data: Schedule }>(`/schedules/${id}/delete`);
    return response.data;
  },

  // Toggle schedule active status
  async toggleScheduleStatus(id: string, isActive: boolean): Promise<Schedule> {
    const response = await apiClient.patch<{ data: Schedule }>(
      `/schedules/${id}/status`,
      { isActive }
    );
    return response.data.data;
  },

  // Get schedules for a specific date range
  async getScheduleByDateRange(
    startDate: string,
    endDate: string
  ): Promise<PaginatedResponse<Schedule>> {
    const response = await apiClient.get<PaginatedResponse<Schedule>>('/schedules', {
      params: { startDate, endDate }
    });
    return response.data;
  },

  async getLandingPageData(date: string): Promise<{
    success: boolean;
    data: LandingPageData;
  }> {
    const response = await apiClient.get('/schedules/landing', {
      params: { date }
    });
    return response.data;
  }

};